import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import uploadService from 'src/services/UploadService'
import { IMPORT_FAILED_TEXT, refusalSentence } from 'src/utils/importRefusal'

// Result files from a tumour test done elsewhere (libra somatic-vendor-files, RB.4b;
// contract of libra #1959).
//
// An archive goes up as a result bundle, any other file as a single result; the
// server refuses the other way round, so the kind follows the file name. With S3
// uploads on, the file goes through the shared multipart upload and is completed
// by the vendor route (detection then runs on the worker and the file reads
// `uploaded` until it finishes); without them it is posted directly and comes back
// already checked. A direct upload over the server's cap moves to the multipart
// route when there is one, and a multipart refusal saying the installation takes
// none moves to the direct route.
//
// Every text the server serves (reason, error, refusal and verification texts) is
// a neutral sentence and is shown as sent; codes are only switched on.
// refusalSentence covers anything unexpected.

const ARCHIVE_NAME = /\.(zip|tar|tgz|tar\.gz|tbz2?|tar\.bz2|tar\.xz|txz)$/i

export const VENDOR_KIND_BUNDLE = 'bundle'
export const VENDOR_KIND_RESULT = 'result'

export const vendorKindOf = (name) => (ARCHIVE_NAME.test(String(name || '')) ? VENDOR_KIND_BUNDLE : VENDOR_KIND_RESULT)

// The S3 storage type of each kind (libra views.S3_FILE_TYPE_TO_STORAGE).
const S3_FILE_TYPE = { [VENDOR_KIND_BUNDLE]: 'vendor_bundle', [VENDOR_KIND_RESULT]: 'vendor_result' }
export const VENDOR_S3_COMPLETE_PATH = '/somatic-vendor-files/s3-complete'

export const STATUS_UPLOADED = 'uploaded'
export const STATUS_DETECTED = 'detected'
export const STATUS_REFUSED = 'refused'
export const STATUS_FAILED = 'failed'

export const UPLOAD_FAILED_TEXT = 'This file could not be uploaded: upload it again.'

// Codes of the direct upload that are the client's own errors, never shown.
const CLIENT_ERROR_CODES = ['no_file', 'invalid_kind']
const TOO_LARGE = 'too_large_for_direct_upload'
const MULTIPART_DISABLED = 'multipart_upload_disabled'

// An upload that failed: an Error carrying the server's code and its sentence.
const uploadError = (error) => {
  const body = error?.response?.data
  const code = typeof body?.code === 'string' ? body.code : null
  const served = typeof body?.error === 'string' ? body.error.trim() : ''
  const text = served && !CLIENT_ERROR_CODES.includes(code) ? served : UPLOAD_FAILED_TEXT
  return Object.assign(new Error(text), { code, text })
}

const directUpload = async (file, kind, onProgress) => {
  const form = new FormData()
  form.append('file', file)
  form.append('kind', kind)
  const { data } = await axios.post(`${API_BASE_URL}/somatic-vendor-files/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100))
    },
  })
  return data
}

const s3Upload = (file, kind, onProgress) =>
  new Promise((resolve, reject) => {
    uploadService
      .startUpload(file, {
        fileCategory: S3_FILE_TYPE[kind],
        metadata: { s3CompletePath: VENDOR_S3_COMPLETE_PATH },
        onProgress: (percentage) => onProgress && onProgress(Math.round(Number(percentage) || 0)),
        onSuccess: (data) => resolve(data),
        onError: (error) => reject(error),
      })
      .then((uploadId) => {
        if (!uploadId) reject(new Error('upload did not start'))
      }, reject)
  })

const s3Enabled = async () => {
  try {
    return Boolean((await uploadService.getS3Config())?.is_s3_enabled)
  } catch (error) {
    return false
  }
}

/**
 * Uploads one result file. Resolves with the server's file record
 * ({ id, kind, status, reason, importer, can_start_analysis, start_refusal,
 * samples, ... }); rejects with an Error carrying { code, text } (the sentence to
 * show) when the upload itself fails.
 */
export const uploadVendorFile = async (file, { onProgress } = {}) => {
  const kind = vendorKindOf(file?.name)
  const s3 = await s3Enabled()
  try {
    if (s3) {
      try {
        return await s3Upload(file, kind, onProgress)
      } catch (error) {
        if (error?.response?.data?.code !== MULTIPART_DISABLED) throw error
      }
      return await directUpload(file, kind, onProgress)
    }
    try {
      return await directUpload(file, kind, onProgress)
    } catch (error) {
      // Over the direct upload's cap: the multipart route, when there is one.
      if (error?.response?.data?.code === TOO_LARGE && (await s3Enabled()))
        return await s3Upload(file, kind, onProgress)
      throw error
    }
  } catch (error) {
    throw uploadError(error)
  }
}

export const fetchVendorFile = async (id) => {
  const { data } = await axios.get(`${API_BASE_URL}/somatic-vendor-files/${id}`)
  return data
}

// A bundle's small-variant file: { member, sample, refusal, samples, registration }.
export const fetchSmallVariants = async (id, sample) => {
  const query = sample ? `?sample=${encodeURIComponent(sample)}` : ''
  const { data } = await axios.get(`${API_BASE_URL}/somatic-vendor-files/${id}/small-variants${query}`)
  return data
}

/**
 * Asks for the file's small variants (a bundle's member, or the file itself) to be
 * registered as an upload, so that a somatic analysis can start from it. 202 (sent)
 * and 200 (already requested or registered) both answer { registration }; a refusal
 * answers 400 { error, code }. Never throws for a refusal.
 * @returns {Promise<{ state: 'requested'|'registered'|'refused', registration: object|null, code?: string, text?: string }>}
 */
export const requestSmallVariants = async (id, sample) => {
  try {
    const { data } = await axios.post(
      `${API_BASE_URL}/somatic-vendor-files/${id}/small-variants`,
      sample ? { sample } : {},
    )
    const registration = data?.registration || null
    return { state: registration?.state || 'requested', registration }
  } catch (error) {
    const response = error?.response
    if (response?.status !== 400) throw error
    const served = typeof response.data?.error === 'string' ? response.data.error.trim() : ''
    return {
      state: 'refused',
      registration: null,
      code: response.data?.code || null,
      text: served || refusalSentence(null),
    }
  }
}

// What a file that can neither start an analysis nor be added reads: the server's
// own sentence. The file's refusal when it was refused; for a small-variant file
// that cannot start (no sample column, for example) why it cannot; else why
// nothing can be imported from it.
export const vendorFileRefusal = (record) => {
  if (!record) return IMPORT_FAILED_TEXT
  if (record.status === STATUS_REFUSED || record.status === STATUS_FAILED) return record.reason || IMPORT_FAILED_TEXT
  if (record.importer?.code === 'small_variant_vcf' && record.start_refusal?.text) return record.start_refusal.text
  if (record.importer && record.importer.available === false) return record.importer.reason || IMPORT_FAILED_TEXT
  return record.start_refusal?.text || IMPORT_FAILED_TEXT
}

// Whether the file can be added to an analysis (a checked file with an importer).
export const canAddToAnalysis = (record) => record?.status === STATUS_DETECTED && record?.importer?.available === true

// Whether an analysis can start from the file: the server says so on the record.
export const canStartAnalysis = (record) => record?.can_start_analysis === true

// Why an analysis cannot start from the file, or null.
export const startRefusalText = (record) => (canStartAnalysis(record) ? null : record?.start_refusal?.text || null)

// What a registration came to, from the GET small-variants answer:
// 'waiting' (requested, or registered with its verification pending), 'ready'
// (verified: start with readyIds), or 'refused' (with the server's text).
export const registrationOutcome = (registration) => {
  if (!registration || registration.state === 'requested') return { state: 'waiting' }
  if (registration.state === 'refused') {
    return { state: 'refused', text: registration.refusal?.text || refusalSentence(null) }
  }
  if (registration.state === 'registered') {
    const verification = registration.verification
    if (!verification || verification.state === 'pending') return { state: 'waiting', checking: true }
    if (verification.state === 'verified' && registration.ready_vcf_file_ids?.length) {
      return { state: 'ready', readyIds: registration.ready_vcf_file_ids }
    }
    return { state: 'refused', text: verification.text || refusalSentence(null) }
  }
  return { state: 'refused', text: refusalSentence(null) }
}
